import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const DEFAULT_OAUTH_CALLBACK_URL =
  'https://app.chattitude.ai/slickbots-ws/api/integrations/oauth/callback';

function zohoOAuthCallbackDisplayUrl(resellerBaseUrl) {
  const t = (resellerBaseUrl || '').trim().replace(/\/+$/, '');
  if (!t) return DEFAULT_OAUTH_CALLBACK_URL;
  return `${t}/slickbots-ws/api/integrations/oauth/callback`;
}

function zohoHomepageUrl(resellerBaseUrl) {
  const t = (resellerBaseUrl || '').trim();
  if (!t) return 'https://chattitude.ai/';
  return t.endsWith('/') ? t : `${t}/`;
}

const ZohoDeskModal = ({
  open,
  onClose,
  productName = 'Chattitude',
  resellerBaseUrl,
}) => {
  const oauthCallbackDisplay = useMemo(
    () => zohoOAuthCallbackDisplayUrl(resellerBaseUrl),
    [resellerBaseUrl]
  );
  const zohoHomepage = useMemo(() => zohoHomepageUrl(resellerBaseUrl), [resellerBaseUrl]);

  const [credentials, setCredentials] = useState({ clientId: '', clientSecret: '' });
  const [deskSettings, setDeskSettings] = useState({ orgId: '', deskBaseUrl: '', departmentId: '' });
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  const authHeader = () => {
    const jwtToken = Cookies.getJwtToken();
    if (jwtToken) return { Authorization: `Bearer ${jwtToken}` };
    const tenantId = Cookies.getTenantId();
    return tenantId ? { Authorization: String(tenantId) } : {};
  };

  useEffect(() => {
    const fetchCredentials = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/integrations/zohodesk/credentials`, { headers: authHeader() });
        setCredentials({
          clientId: res.data?.clientId || '',
          clientSecret: res.data?.clientSecret || '',
        });
        setDeskSettings({
          orgId: res.data?.orgId || '',
          deskBaseUrl: res.data?.deskBaseUrl || '',
          departmentId: res.data?.departmentId || '',
        });
      } catch (e) {
        setCredentials({ clientId: '', clientSecret: '' });
        setDeskSettings({ orgId: '', deskBaseUrl: '', departmentId: '' });
        setError('Failed to fetch credentials.');
      } finally {
        setLoading(false);
      }
    };

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/integrations/zohodesk/status`, { headers: authHeader() });
        setConnected(!!res.data?.connected);
      } catch (e) {
        setConnected(false);
      }
    };

    if (open) {
      fetchCredentials();
      fetchStatus();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCredChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeskChange = (e) => {
    const { name, value } = e.target;
    setDeskSettings((prev) => ({ ...prev, [name]: value }));
  };

  const validateCreds = () => {
    if (!credentials.clientId) return 'Client ID is required.';
    if (!credentials.clientSecret) return 'Client Secret is required.';
    return null;
  };

  const persistDeskSettings = async () => {
    await axios.post(
      `${API_URL}/integrations/zohodesk/settings`,
      {
        orgId: deskSettings.orgId || '',
        deskBaseUrl: deskSettings.deskBaseUrl || '',
        departmentId: deskSettings.departmentId || '',
      },
      { headers: authHeader() }
    );
  };

  const handleSave = async () => {
    const validationError = validateCreds();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/integrations/zohodesk/credentials`, credentials, { headers: authHeader() });
      await persistDeskSettings();
      setSnackbar({ open: true, message: 'Saved successfully.', severity: 'success' });
      setTimeout(() => onClose && onClose(), 1200);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save.', severity: 'error' });
      setError('Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    const validationError = validateCreds();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    if (!deskSettings.orgId || !deskSettings.orgId.trim()) {
      setSnackbar({
        open: true,
        message: 'Zoho Desk Organization ID is required for MCP tools (find it in Zoho Desk admin / API).',
        severity: 'error',
      });
      return;
    }
    if (!deskSettings.departmentId || !deskSettings.departmentId.trim()) {
      setSnackbar({
        open: true,
        message:
          'Default department ID is required for creating tickets (Setup → Departments in Zoho Desk, or GET /api/v1/departments).',
        severity: 'error',
      });
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/integrations/zohodesk/credentials`, credentials, { headers: authHeader() });
      await persistDeskSettings();

      const res = await axios.get(`${API_URL}/integrations/zohodesk/authorize-url`, { headers: authHeader() });
      const url = res.data?.url;
      if (!url) throw new Error('Missing authorize URL');

      window.open(url, '_blank');

      const poll = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_URL}/integrations/zohodesk/status`, { headers: authHeader() });
          if (statusRes.data?.connected) {
            setConnected(true);
            setConnecting(false);
            clearInterval(poll);
            setSnackbar({ open: true, message: 'Zoho Desk connected!', severity: 'success' });
          }
        } catch (e) {
          // ignore while polling
        }
      }, 2000);
      setPollInterval(poll);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to initiate Zoho Desk OAuth.', severity: 'error' });
      setError('Failed to initiate Zoho Desk OAuth.');
      setConnecting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ zIndex: 99999 }}
      PaperProps={{ style: { minWidth: 1200, minHeight: 800, zIndex: 99999 } }}
    >
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        Zoho Desk (MCP)
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Client ID"
              name="clientId"
              value={credentials.clientId}
              onChange={handleCredChange}
              required
              fullWidth
            />
            <TextField
              label="Client Secret"
              name="clientSecret"
              value={credentials.clientSecret}
              onChange={handleCredChange}
              required
              fullWidth
            />
            <TextField
              label="Zoho Desk Organization ID"
              name="orgId"
              value={deskSettings.orgId}
              onChange={handleDeskChange}
              required
              fullWidth
              helperText="Required for MCP tools. Available in Zoho Desk (Admin → Developer Space / API) or your Desk URL context."
            />
            <TextField
              label="Desk API base URL (optional)"
              name="deskBaseUrl"
              value={deskSettings.deskBaseUrl}
              onChange={handleDeskChange}
              fullWidth
              placeholder="https://desk.zoho.com"
              helperText="Leave blank for US (desk.zoho.com). Use e.g. https://desk.zoho.eu for EU data center."
            />
            <TextField
              label="Default department ID"
              name="departmentId"
              value={deskSettings.departmentId}
              onChange={handleDeskChange}
              required
              fullWidth
              helperText="Required for zohodesk_create_ticket. Use the string ID from Zoho Desk (not a rounded number)."
            />

            <Box sx={{ mt: 2, mb: 2, p: 2, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Connect Zoho Desk
              </Typography>
              <ol style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>
                  In{' '}
                  <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer">
                    Zoho API Console
                  </a>
                  , create a <b>Server-based</b> client.
                </li>
                <li>
                  Homepage URL: <b>{zohoHomepage}</b>
                </li>
                <li>
                  Authorized redirect URI (must match exactly):
                  <ul style={{ marginTop: 4 }}>
                    <li>
                      <b>{oauthCallbackDisplay}</b>
                    </li>
                  </ul>
                </li>
                <li>Copy Client ID and Client Secret into the fields above, set Organization ID and Default department ID, then Connect.</li>
                <li>Scopes are requested automatically (Desk tickets &amp; contacts). After OAuth completes, attach Zoho Desk to your agent under Tools Integrations.</li>
              </ol>
            </Box>

            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 2, px: 3 }}>
        <Button          onClick={handleSave}
          variant="outlined"
          color="primary"
          disabled={loading || connecting}
          sx={{ minWidth: 180, py: 1.2 }}
        >
          Save &amp; Close
        </Button>
        <Button
          onClick={handleConnect}
          variant="contained"
          color="success"
          disabled={
            connecting ||
            loading ||
            !credentials.clientId ||
            !credentials.clientSecret ||
            !deskSettings.orgId?.trim() ||
            !deskSettings.departmentId?.trim()
          }
          sx={{ minWidth: 220, py: 1.2 }}
        >
          {connected ? 'Connected to Zoho Desk' : connecting ? 'Connecting...' : 'Connect to Zoho Desk'}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ZohoDeskModal;
